import { useRef } from 'react';
import { T } from 'gt-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { CellRenderer } from './CellRenderer';
import type { SheetData } from './types';

interface VirtualizedTableProps {
  sheet: SheetData;
  height?: number;
  expanded?: boolean;
}

const ROW_HEIGHT = 24;
const HEADER_HEIGHT = 28;
const ROW_NUMBER_WIDTH = 48;

export function VirtualizedTable({
  sheet,
  height = 400,
  expanded = false,
}: VirtualizedTableProps) {
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: sheet.rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  if (sheet.rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
        <T>No data in this sheet</T>
      </div>
    );
  }

  const columnWidths = expanded
    ? sheet.expandedColumnWidths
    : sheet.columnWidths;
  const totalWidth =
    ROW_NUMBER_WIDTH + columnWidths.reduce((sum, w) => sum + w, 0);

  return (
    <div ref={parentRef} className="overflow-auto" style={{ height }}>
      <div style={{ minWidth: totalWidth }}>
        <div
          className="flex sticky top-0 z-10 bg-muted font-mono text-xs border-b border-r"
          style={{ width: totalWidth, height: HEADER_HEIGHT }}
        >
          <div
            className="px-2 py-1.5 text-center text-muted-foreground border-r font-medium flex items-center justify-center shrink-0"
            style={{ width: ROW_NUMBER_WIDTH }}
          >
            #
          </div>
          {sheet.headers.map((header, colIndex) => {
            const isLast = colIndex === sheet.headers.length - 1;

            return (
              <Tooltip key={colIndex}>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'px-2 py-1.5 font-medium flex items-center shrink-0 overflow-hidden whitespace-nowrap',
                      !expanded && 'text-ellipsis',
                      !isLast && 'border-r',
                    )}
                    style={{ width: columnWidths[colIndex] }}
                  >
                    {header}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="font-mono text-xs">
                  {header}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        <div
          style={{
            height: rowVirtualizer.getTotalSize(),
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = sheet.rows[virtualRow.index];
            return (
              <div
                key={virtualRow.index}
                className={cn(
                  'flex absolute font-mono text-xs border-b border-r',
                  virtualRow.index % 2 === 0 ? 'bg-background' : 'bg-muted/20',
                )}
                style={{
                  height: ROW_HEIGHT,
                  transform: `translateY(${virtualRow.start}px)`,
                  width: totalWidth,
                }}
              >
                <div
                  className="px-2 py-0.5 text-center text-muted-foreground border-r bg-muted/30 flex items-center justify-center shrink-0"
                  style={{ width: ROW_NUMBER_WIDTH }}
                >
                  {virtualRow.index + 1}
                </div>
                {row.map((cell, colIndex) => {
                  const cellValue = cell.value;
                  const cellText = cellValue !== null ? String(cellValue) : '';
                  const colWidth = columnWidths[colIndex];
                  const needsTooltip =
                    !expanded && cellText.length * 7 > colWidth - 16;
                  const isNumber = cell.type === 'number';
                  const isLast = colIndex === row.length - 1;

                  const cellDiv = (
                    <div
                      key={colIndex}
                      className={cn(
                        'px-2 py-0.5 shrink-0 overflow-hidden whitespace-nowrap',
                        !expanded && 'text-ellipsis',
                        !isLast && 'border-r',
                        isNumber && 'text-right',
                      )}
                      style={{ width: colWidth }}
                    >
                      <CellRenderer cell={cell} />
                    </div>
                  );

                  if (needsTooltip) {
                    return (
                      <Tooltip key={colIndex}>
                        <TooltipTrigger asChild>{cellDiv}</TooltipTrigger>
                        <TooltipContent
                          side="bottom"
                          className="max-w-md whitespace-pre-wrap font-mono text-xs"
                        >
                          {cellText}
                        </TooltipContent>
                      </Tooltip>
                    );
                  }

                  return cellDiv;
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
